import type { PromoCandidate } from '@supply-mover-2-0/shared';
import { useEffect, useState } from 'react';
import { PromoTile } from './PromoTile';

/**
 * Auto-rotating carousel of promo tiles. Default rotation interval is
 * 4.5 seconds; mouse-hover pauses. Empty when no candidates were
 * selected.
 *
 * Transition is a horizontal wipe — the entire promo strip is
 * rendered as a flex row and translateX'd into view as the active
 * index advances. The wipe is short (~500ms) so customers feel
 * forward motion without losing the current slide. Hard cuts (the
 * earlier render-only-current approach) read as broken when the
 * page is auto-advancing every few seconds — eyes can't track the
 * change without the wipe.
 *
 * 3s was the original default and felt frantic when a customer was
 * trying to read offer copy / fine print on a slide; the wipe was
 * still in flight when the next advance fired. 4.5s gives roughly
 * one breath per slide while keeping perceptible forward motion.
 */
export function PromoCarouselWidget({
	promos,
	rotateMs = 4500,
	density,
	onLikelyStaleUrl,
}: {
	promos: Array<PromoCandidate>;
	/**
	 * Time between slides in milliseconds. Pass `0` (or anything <
	 * 100ms) to disable auto-advance entirely — the customer must
	 * use the dot indicators to scroll. Defaults to 4.5s.
	 */
	rotateMs?: number;
	density?: 'standard' | 'compact';
	/** Forwarded to each PromoTile. See `PromoTile.onLikelyStaleUrl`. */
	onLikelyStaleUrl?: () => void;
}): JSX.Element | null {
	const [index, setIndex] = useState(0);
	const [paused, setPaused] = useState(false);

	useEffect(() => {
		if (paused || promos.length <= 1 || rotateMs < 100) return;
		const t = setInterval(() => {
			setIndex((i) => (i + 1) % promos.length);
		}, rotateMs);
		return () => clearInterval(t);
	}, [paused, promos.length, rotateMs]);

	useEffect(() => {
		// Reset when the candidate list changes (e.g. cart change forced
		// a refetch); avoids the index pointing at a removed promo.
		if (index >= promos.length) {
			setIndex(0);
		}
	}, [promos.length, index]);

	if (promos.length === 0) return null;

	return (
		// Outer: full-width frame that clips the strip. Hover/leave
		// pauses the auto-advance so a customer hovering to read the
		// fine print isn't yanked to the next slide.
		<div
			className="relative w-full h-full overflow-hidden"
			onMouseEnter={() => setPaused(true)}
			onMouseLeave={() => setPaused(false)}
		>
			{/*
			 * Inner strip: width = N × 100% of the frame (one slide
			 * worth per promo); translateX pulls the active slide into
			 * view. `transition-transform duration-500 ease-out` is the
			 * actual wipe — feels like a flick from the right at default
			 * 3s cadence. The strip itself uses a CSS Grid (one column
			 * per promo, each fills the frame) so each PromoTile gets
			 * the full hero width and the wipe is exactly 100% per slot.
			 */}
			<div
				className="grid h-full transition-transform duration-500 ease-out"
				style={{
					gridTemplateColumns: `repeat(${promos.length}, 100%)`,
					transform: `translateX(-${index * 100}%)`,
				}}
			>
				{promos.map((p) => (
					<div key={p.id} className="w-full h-full">
						{/*
						 * Hero size — the carousel is the hero placement, so
						 * each slide renders at the 4:1 aspect-locked tile
						 * that uses `bannerUrl` (3:1 native art letterboxed
						 * inside 4:1).
						 */}
						<PromoTile
							promo={p as never}
							size="hero"
							density={density}
							onLikelyStaleUrl={onLikelyStaleUrl}
						/>
					</div>
				))}
			</div>
			{promos.length > 1 && (
				<div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
					{promos.map((p, i) => (
						<button
							key={p.id}
							type="button"
							onClick={() => setIndex(i)}
							aria-label={`Show promo ${i + 1}`}
							className={`w-2 h-2 rounded-full transition-colors ${
								i === index ? 'bg-white' : 'bg-white/40 hover:bg-white/70'
							}`}
						/>
					))}
				</div>
			)}
		</div>
	);
}
